import { queryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { DropdownUser } from '@/api/capa/types';
import { fetchCertificationStandards } from '@/api/certifications';
import {
  fetchAllCompaniesDropdown,
  fetchAllCountries,
  fetchCollaborationStatuses,
  fetchCoworkerOptions,
  fetchFacilityProcessOptions,
  fetchNetworkCompaniesDropdown,
  fetchParentStyleOptions,
  fetchParentVariantOptions,
  fetchProductCategories,
  fetchRelationshipTypes,
  fetchStyleTypeListPage,
  fetchUsersByCompanyIds,
} from '@/api/dropdowns';
import type { CompanyOption } from '@/api/my-styles/types';
import type { FilterOption } from '@/api/orders/types';

const STALE_REFERENCE = 24 * 60 * 60 * 1000;
const STALE_DYNAMIC = 5 * 60 * 1000;

export function useCountryOptions() {
  return useQuery({
    queryKey: ['options', 'countries'],
    queryFn: async () => {
      const countries = await fetchAllCountries();
      return countries.map(c => ({
        key: c.countryCode,
        label: c.countryNameEn,
        name: c.countryNameEn,
      }));
    },
    staleTime: STALE_REFERENCE,
  });
}

export function useFacilityProcessOptions() {
  return useQuery({
    queryKey: ['options', 'facility-processes'],
    queryFn: async () => {
      const res = await fetchFacilityProcessOptions();
      const options: FilterOption[] = [];
      for (const group of res?.facilities ?? []) {
        for (const child of group.children) {
          options.push({ key: child.key, label: child.text });
        }
      }
      return options;
    },
    staleTime: STALE_REFERENCE,
  });
}

export function useCollaborationStatusOptions() {
  return useQuery({
    queryKey: ['options', 'collaboration-statuses'],
    queryFn: async () => {
      const res = await fetchCollaborationStatuses();
      return (res?.data ?? []).map(s => ({ key: s.id, label: s.name }));
    },
    staleTime: STALE_REFERENCE,
  });
}

export function useRelationshipTypeOptions() {
  return useQuery({
    queryKey: ['options', 'relationship-types'],
    queryFn: async () => {
      const res = await fetchRelationshipTypes();
      return (res?.data ?? []).map(r => ({ key: r.id, label: r.name }));
    },
    staleTime: STALE_REFERENCE,
  });
}

export function networkCompanyOptionsQueryOptions() {
  return queryOptions({
    queryKey: ['options', 'companies-dropdown'] as const,
    queryFn: async () => {
      const res = await fetchNetworkCompaniesDropdown();
      return (res?.data ?? []).map(c => ({
        key: c.id,
        label: c.displayName || c.name || c.officialName,
        country: c.country ?? null,
      }));
    },
    staleTime: STALE_DYNAMIC,
  });
}

export function useNetworkCompanyOptions() {
  return useQuery(networkCompanyOptionsQueryOptions());
}

export function useAllCompanyOptions() {
  return useQuery({
    queryKey: ['options', 'all-companies-dropdown'] as const,
    queryFn: async () => {
      const res = await fetchAllCompaniesDropdown();
      return (res?.data ?? []).map(c => ({
        key: c.id,
        label: c.displayName || c.name || c.officialName,
      }));
    },
    staleTime: STALE_DYNAMIC,
  });
}

export function useResponsiblePersonOptions(_companyId?: string) {
  return useQuery({
    queryKey: ['options', 'responsible-persons'],
    queryFn: async () => {
      const res = await fetchCoworkerOptions();
      const seen = new Set<string>();
      return (res?.data ?? [])
        .filter((u: DropdownUser) => {
          const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.toLowerCase();
          return !name.includes('anonymized') && !/\*/.test(name);
        })
        .filter((u: DropdownUser) => {
          const label = `${u.firstName ?? ''} ${u.lastName ?? ''}`
            .trim()
            .toLowerCase();
          if (seen.has(label)) return false;
          seen.add(label);
          return true;
        })
        .map((u: DropdownUser) => ({
          key: u.id,
          label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
        }));
    },
    staleTime: STALE_DYNAMIC,
  });
}

export function useUsersByCompanyIds(companyIds: string) {
  return useQuery({
    queryKey: ['options', 'users-by-company-ids', companyIds],
    queryFn: async () => {
      const users = await fetchUsersByCompanyIds(companyIds);
      const seen = new Set<string>();
      return (users ?? [])
        .filter(u => {
          const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.toLowerCase();
          return !name.includes('anonymized') && !/\*/.test(name);
        })
        .filter(u => {
          const label = `${u.firstName ?? ''} ${u.lastName ?? ''}`
            .trim()
            .toLowerCase();
          if (seen.has(label)) return false;
          seen.add(label);
          return true;
        })
        .map(u => ({
          key: u.id,
          label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
        }));
    },
    enabled: !!companyIds,
    staleTime: STALE_DYNAMIC,
  });
}

export function useCertificationStandardOptions() {
  return useQuery({
    queryKey: ['options', 'certification-standards'],
    queryFn: async () => {
      const res = await fetchCertificationStandards();
      return (res?.data ?? []).filter(
        s => s.type !== 'COMPANY_DOCUMENT' && s.type !== 'AUDIT',
      );
    },
    staleTime: STALE_REFERENCE,
  });
}

export function styleTypeOptionsQueryOptions() {
  return queryOptions({
    queryKey: ['options', 'style-types'] as const,
    queryFn: async () => {
      const LIMIT = 200;
      const first = await fetchStyleTypeListPage({ page: 1, limit: LIMIT });
      const totalPages = first?.metadata?.pagination?.totalPages ?? 1;
      let results = first?.data ?? [];
      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            fetchStyleTypeListPage({ page: i + 2, limit: LIMIT }),
          ),
        );
        results = [...results, ...rest.flatMap(r => r?.data ?? [])];
      }
      return results.map(s => ({ key: s.id, label: s.name }));
    },
    staleTime: STALE_REFERENCE,
  });
}

export function useStyleTypeOptions(enabled = true) {
  return useQuery({ ...styleTypeOptionsQueryOptions(), enabled });
}

export function useParentStyleOptions(companyId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['options', 'parent-styles', companyId],
    queryFn: async () => {
      const res = await fetchParentStyleOptions({
        page: 1,
        limit: 200,
        companyId,
        isArchived: false,
      });
      return (res?.data ?? []).map(s => ({ key: s.id, label: s.name }));
    },
    enabled: enabled && !!companyId,
    staleTime: STALE_DYNAMIC,
  });
}

export function useParentVariantOptions(companyId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['options', 'parent-variants', companyId],
    queryFn: async () => {
      const res = await fetchParentVariantOptions({
        page: 1,
        limit: 200,
        companyId,
        isArchived: false,
      });
      return (res?.data ?? []).map(v => ({ key: v.id, label: v.name }));
    },
    enabled: enabled && !!companyId,
    staleTime: STALE_DYNAMIC,
  });
}

export function useNetworkCompanyMap(): Map<string, CompanyOption> {
  const { data } = useQuery({
    queryKey: ['options', 'companies-dropdown-with-country'],
    queryFn: async () => {
      const res = await fetchNetworkCompaniesDropdown();
      return (res?.data ?? []).map(c => ({
        key: c.id,
        label: c.officialName || c.name,
        country: c.country ?? null,
      }));
    },
    staleTime: STALE_DYNAMIC,
  });
  return useMemo(() => new Map((data ?? []).map(c => [c.key, c])), [data]);
}

export function useProductCategoryOptions() {
  const { data: categories } = useQuery({
    queryKey: ['options', 'product-categories'],
    queryFn: async () => {
      const res = await fetchProductCategories();
      return res?.data ?? [];
    },
    staleTime: STALE_REFERENCE,
  });

  const productLevels = (categories ?? [])
    .filter(c => c.parentId === null)
    .map(c => ({ key: c.id, name: c.name }));

  const productTypes = (categories ?? [])
    .filter(c => c.parentId !== null)
    .map(c => ({ key: c.id, name: c.name }));

  return { productLevels, productTypes };
}
